import React from 'react'
// Checking

type ColorToken = {
	name: string
	className: string
	description?: string
}

const surfaceTokens: ColorToken[] = [
	{
		name: 'background',
		className: 'bg-background text-foreground',
		description: 'App background',
	},
	{
		name: 'card',
		className: 'bg-card text-card-foreground',
		description: 'Card background',
	},
	{
		name: 'popover',
		className: 'bg-popover text-popover-foreground',
		description: 'Popover / menu background',
	},
	{
		name: 'sidebar',
		className: 'bg-sidebar text-sidebar-foreground',
		description: 'Sidebar background',
	},
]

const primaryTokens: ColorToken[] = [
	{
		name: 'primary',
		className: 'bg-primary text-primary-foreground',
		description: 'Primary brand color',
	},
	{
		name: 'secondary',
		className: 'bg-secondary text-secondary-foreground',
		description: 'Secondary brand color',
	},
	{
		name: 'accent',
		className: 'bg-accent text-accent-foreground',
		description: 'Accent / emphasis',
	},
	{
		name: 'main-accent',
		className: 'bg-main-accent text-main-accent-foreground',
		description: 'Main accent (custom)',
	},
	{
		name: 'accent-main',
		className: 'bg-accent-main text-main-accent-foreground',
		description: 'Alternative accent',
	},
]

const contentTokens: ColorToken[] = [
	{
		name: 'foreground',
		className: 'bg-foreground text-background',
		description: 'Default text on background',
	},
	{
		name: 'content-secondary',
		className: 'bg-content-secondary text-background',
		description: 'Secondary text',
	},
	{
		name: 'content-tertiary',
		className: 'bg-content-tertiary text-background',
		description: 'Tertiary / subtle text',
	},
	{
		name: 'content-disabled',
		className: 'bg-content-disabled text-background',
		description: 'Disabled text',
	},
]

const statusTokens: ColorToken[] = [
	{
		name: 'status-success',
		className: 'bg-status-success text-background',
		description: 'Success state',
	},
	{
		name: 'status-info',
		className: 'bg-status-info text-background',
		description: 'Info state',
	},
	{
		name: 'status-warning',
		className: 'bg-status-warning text-background',
		description: 'Warning state',
	},
	{
		name: 'status-inactive',
		className: 'bg-status-inactive text-background',
		description: 'Inactive / neutral',
	},
]

const feedbackTokens: ColorToken[] = [
	{
		name: 'feedback-success',
		className: 'bg-feedback-success text-background',
		description: 'Feedback success',
	},
	{
		name: 'feedback-warning',
		className: 'bg-feedback-warning text-background',
		description: 'Feedback warning',
	},
	{
		name: 'feedback-error',
		className: 'bg-feedback-error text-background',
		description: 'Feedback error',
	},
]

const borderTokens: ColorToken[] = [
	{
		name: 'border',
		className: 'bg-border',
		description: 'Default border',
	},
	{
		name: 'sidebar-border',
		className: 'bg-sidebar-border',
		description: 'Sidebar border',
	},
	{
		name: 'ring',
		className: 'bg-ring',
		description: 'Default ring',
	},
	{
		name: 'sidebar-ring',
		className: 'bg-sidebar-ring',
		description: 'Sidebar ring',
	},
]

const shadeTokens: ColorToken[] = [
	{
		name: 'shade',
		className: 'bg-shade',
	},
	{
		name: 'shade-light',
		className: 'bg-shade-light',
	},
]

const chartTokens: ColorToken[] = [
	{ name: 'chart-1', className: 'bg-chart-1' },
	{ name: 'chart-2', className: 'bg-chart-2' },
	{ name: 'chart-3', className: 'bg-chart-3' },
	{ name: 'chart-4', className: 'bg-chart-4' },
	{ name: 'chart-5', className: 'bg-chart-5' },
]

const allTokens: ColorToken[] = [
	...surfaceTokens,
	...primaryTokens,
	...contentTokens,
	...statusTokens,
	...feedbackTokens,
	...borderTokens,
	...shadeTokens,
	...chartTokens,
]

const radiusTokens = ['rounded-sm', 'rounded-md', 'rounded-lg', 'rounded-xl']

const Section: React.FC<{ title: string; tokens: ColorToken[] }> = ({
	title,
	tokens,
}) => (
	<section className="space-y-3">
		<h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
			{title}
		</h2>
		<div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
			{tokens.map((token) => (
				<div
					key={token.name}
					className={
						'flex flex-col justify-between rounded-lg border border-border p-3 text-xs bg-card shadow-sm'
					}
				>
					<div
						className={`mb-3 flex h-20 items-center justify-center rounded-md text-xs font-medium ${token.className}`}
					>
						{token.name}
					</div>
					<div className="space-y-0.5 text-[11px] text-muted-foreground">
						<div className="font-medium text-foreground">{token.name}</div>
						<div className="font-mono text-[10px]">
							bg-{token.name}
							{token.name.endsWith('foreground') ? '' : ', text-' + token.name}
						</div>
						{token.description && (
							<div className="text-[10px]">{token.description}</div>
						)}
					</div>
				</div>
			))}
		</div>
	</section>
)

export const ThemePreview: React.FC = () => {
	return (
		<div className="min-h-screen bg-background text-foreground px-6 py-8 md:px-10 lg:px-16">
			<div className="mx-auto flex max-w-6xl flex-col gap-8">
				<header className="space-y-2">
					<h1 className="text-2xl font-bold tracking-tight">
						Design theme preview
					</h1>
					<p className="max-w-2xl text-sm text-muted-foreground">
						This view shows the Tailwind theme tokens defined in
						&apos;index.css&apos;. Toggle light/dark mode to see how the same
						classes adapt.
					</p>
				</header>

				<main className="space-y-8">
					<Section title="All colors" tokens={allTokens} />
					<Section title="Surfaces" tokens={surfaceTokens} />
					<Section title="Primary & accents" tokens={primaryTokens} />
					<Section title="Content / typography" tokens={contentTokens} />
					<Section title="Status" tokens={statusTokens} />
					<Section title="Feedback" tokens={feedbackTokens} />
					<Section title="Borders & rings" tokens={borderTokens} />
					<Section title="Shades" tokens={shadeTokens} />

					<section className="space-y-3">
						<h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
							Charts
						</h2>
						<div className="flex flex-wrap gap-3">
							{chartTokens.map((token) => (
								<div
									key={token.name}
									className="flex flex-col items-center gap-1 text-xs"
								>
									<div
										className={`h-10 w-10 rounded-full border border-border ${token.className}`}
									/>
									<span className="font-mono text-[11px] text-muted-foreground">
										bg-{token.name}
									</span>
								</div>
							))}
						</div>
					</section>

					<section className="space-y-3">
						<h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
							Radius scale
						</h2>
						<div className="flex flex-wrap gap-4">
							{radiusTokens.map((radius) => (
								<div key={radius} className="flex flex-col gap-1 text-xs">
									<div
										className={`h-10 w-20 bg-primary text-primary-foreground ${radius} flex items-center justify-center`}
									>
										{radius.replace('rounded-', '')}
									</div>
									<span className="font-mono text-[11px] text-muted-foreground">
										{radius}
									</span>
								</div>
							))}
						</div>
					</section>
				</main>
			</div>
		</div>
	)
}

export default ThemePreview
